<template>
    <div class="student-portal-certificates" :class="{ dark }">
        <div class="student-portal-certificates__inner">
            <header class="student-portal-certificates__header">
                <div class="student-portal-certificates__header-main">
                    <h1 class="card-title">Meus Certificados</h1>
                    <p class="card-subtitle">
                        Visualize e baixe os certificados dos cursos que você concluiu
                    </p>
                </div>
                <v-btn
                    color="#1ac4cd"
                    variant="elevated"
                    prepend-icon="mdi-refresh"
                    class="student-portal-certificates__refresh-btn"
                    :loading="isLoading"
                    @click="loadCertificates"
                >
                    Atualizar
                </v-btn>
            </header>

            <v-card
                v-if="isLoading && !hasReady"
                class="portal-certificates__card portal-certificates__card--surface pa-8"
                elevation="0"
            >
                <v-row justify="center">
                    <v-col cols="auto">
                        <v-progress-circular
                            indeterminate
                            color="primary"
                            size="64"
                        ></v-progress-circular>
                        <p class="text-center mt-4 student-portal-certificates__loading-text">
                            Carregando certificados...
                        </p>
                    </v-col>
                </v-row>
            </v-card>

            <v-card
                v-else
                elevation="0"
                class="portal-certificates__card portal-certificates__card--surface p-2 p-md-4"
            >
                <div class="portal-certificates__filters mb-4">
                    <v-text-field
                        v-model.trim="filters.query"
                        class="portal-certificates__filter-search"
                        label="Buscar por nome do curso"
                        variant="outlined"
                        density="comfortable"
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        hide-details
                    />
                    <v-select
                        v-model="filters.period"
                        class="portal-certificates__filter-period"
                        :items="periodFilterOptions"
                        item-title="label"
                        item-value="value"
                        label="Período"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                    />
                    <v-btn
                        variant="tonal"
                        color="grey-darken-1"
                        prepend-icon="mdi-filter-off-outline"
                        :disabled="!hasActiveFilters"
                        @click="clearFilters"
                    >
                        Limpar
                    </v-btn>
                </div>

                <div class="portal-certificates__filters-summary mb-3">
                    <span>
                        {{ filteredCertificates.length }}
                        {{
                            filteredCertificates.length === 1
                                ? 'certificado encontrado'
                                : 'certificados encontrados'
                        }}
                    </span>
                </div>

                <v-data-table
                    :headers="headers"
                    :items="filteredCertificates"
                    :items-per-page="itemsPerPage"
                    :hide-default-footer="shouldHidePagination"
                    class="certificates-table elevation-0"
                    no-data-text="Nenhum certificado disponível"
                >
                    <template v-slot:[`item.course`]="{ item }">
                        <div class="d-flex align-center py-2">
                            <v-avatar
                                v-if="item.courseImagePathFinal"
                                size="60"
                                rounded="lg"
                                class="mr-4 d-none d-md-block"
                            >
                                <v-img
                                    :src="item.courseImagePathFinal"
                                    :alt="item.courseName"
                                    cover
                                ></v-img>
                            </v-avatar>
                            <div>
                                <div class="font-weight-medium text-body-1 text-left">
                                    {{ item.courseName }}
                                </div>
                            </div>
                        </div>
                    </template>

                    <template v-slot:[`item.completionDate`]="{ item }">
                        <div class="font-weight-medium d-none d-md-block">
                            {{ formatDate(item.dateCertificate) }}
                        </div>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                        <v-btn
                            color="#19c7d0"
                            variant="elevated"
                            prepend-icon="mdi-certificate"
                            @click="viewCertificate(item.courseId)"
                            :loading="isLoading"
                        >
                            Ver Certificado
                        </v-btn>
                    </template>

                    <template v-slot:no-data>
                        <div class="text-center pa-8">
                            <v-icon
                                size="80"
                                color="grey-lighten-1"
                                class="mb-4"
                            >
                                mdi-school
                            </v-icon>
                            <h3 class="text-h5 mb-2">
                                Nenhum certificado disponível
                            </h3>
                            <p class="text-body-1 text-medium-emphasis">
                                Complete seus cursos para gerar certificados
                            </p>
                        </div>
                    </template>
                </v-data-table>
            </v-card>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import apiCertificates from '@/utils/portal/certificate.js';
    import { toast } from 'vue3-toastify';

    export default {
        name: 'StudentCertificates',
        data() {
            return {
                isLoading: false,
                hasReady: false,
                dataCertificates: [],
                itemsPerPage: 10,
                filters: {
                    query: '',
                    period: 'all',
                },
                periodFilterOptions: [
                    { label: 'Todo período', value: 'all' },
                    { label: 'Últimos 7 dias', value: 7 },
                    { label: 'Últimos 30 dias', value: 30 },
                    { label: 'Últimos 90 dias', value: 90 },
                ],
                headers: [
                    {
                        title: 'Curso',
                        key: 'course',
                        sortable: false,
                        width: '40%',
                        minWidth: 200,
                        align: 'start',
                    },
                    {
                        title: 'Data de Conclusão',
                        key: 'completionDate',
                        sortable: true,
                        width: '20%',
                        align: 'center',
                        class: 'd-none d-md-table-cell',
                    },
                    {
                        title: 'Ações',
                        key: 'actions',
                        sortable: false,
                        width: '25%',
                        align: 'center',
                    },
                ],
            };
        },
        computed: {
            ...mapState(['token', 'dark']),
            hasActiveFilters() {
                return (
                    this.filters.query !== '' ||
                    this.filters.period !== 'all'
                );
            },
            shouldHidePagination() {
                return this.filteredCertificates.length <= this.itemsPerPage;
            },
            filteredCertificates() {
                const q = this.filters.query.toLowerCase();
                const period = this.filters.period;
                const now = new Date();

                return this.dataCertificates.filter((item) => {
                    const byQuery =
                        !q ||
                        String(item.courseName || '')
                            .toLowerCase()
                            .includes(q);

                    let byPeriod = true;
                    if (period !== 'all' && item.dateCertificate) {
                        const dt = new Date(item.dateCertificate);
                        if (Number.isNaN(dt.getTime())) {
                            byPeriod = false;
                        } else {
                            const diffMs = now.getTime() - dt.getTime();
                            const diffDays = diffMs / (1000 * 60 * 60 * 24);
                            byPeriod = diffDays <= Number(period);
                        }
                    }

                    return byQuery && byPeriod;
                });
            },
        },
        created() {
            this.loadCertificates();
        },
        methods: {
            async loadCertificates() {
                try {
                    this.isLoading = true;
                    const certificates =
                        await apiCertificates.getPortalCertificates(this.token);
                    this.dataCertificates = certificates;
                } catch (error) {
                    toast.error('Erro ao carregar os certificados.');
                    throw error;
                } finally {
                    this.isLoading = false;
                    this.hasReady = true;
                }
            },

            viewCertificate(courseId) {
                // Navega para a rota do certificado com o ID do curso
                this.$router.push(`/aluno/certificado/${courseId}`);
            },

            formatDate(date) {
                if (!date) return 'Data não disponível';

                try {
                    const dateObj = new Date(date);
                    return dateObj.toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                    });
                } catch (error) {
                    return 'Data inválida';
                }
            },
            clearFilters() {
                this.filters.query = '';
                this.filters.period = 'all';
            },
        },
    };
</script>

<style scoped>
    .student-portal-certificates {
        --dash-bg: #eef0f4;
        --dash-bg-elevated: #ffffff;
        --dash-text: #111827;
        --dash-text-muted: #6b7280;
        --dash-border: rgba(15, 23, 42, 0.08);
        min-height: 100%;
        background-color: var(--dash-bg);
        box-sizing: border-box;
    }

    .student-portal-certificates.dark {
        --dash-bg: #0f0e12;
        --dash-bg-elevated: #1a191f;
        --dash-text: #f3f4f6;
        --dash-text-muted: #9ca3af;
        --dash-border: rgba(255, 255, 255, 0.08);
    }

    .student-portal-certificates__inner {
        max-width: 1920px;
        margin: 0 auto;
        padding: 18px 14px 32px;
        box-sizing: border-box;
    }

    .student-portal-certificates__header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        text-align: left;
        margin-bottom: 28px;
    }

    .student-portal-certificates__header-main {
        min-width: 0;
    }

    .student-portal-certificates__refresh-btn {
        flex-shrink: 0;
        margin-top: 4px;
    }

    .student-portal-certificates__loading-text {
        color: var(--dash-text-muted);
    }

    .portal-certificates__card--surface {
        background-color: var(--dash-bg-elevated) !important;
        color: var(--dash-text);
        border: 1px solid #e8eaf0;
        border-radius: 12px;
        box-shadow:
            0 1px 3px rgba(37, 8, 145, 0.06),
            0 6px 22px rgba(15, 23, 42, 0.06);
    }

    .portal-certificates__card {
        padding: 14px !important;
    }

    :deep(.certificates-table) {
        border: 1px solid #e8eaf0;
        border-radius: 10px;
        overflow: hidden;
    }

    :deep(.certificates-table .v-data-table__th) {
        background: linear-gradient(180deg, #fafbff 0%, #ffffff 100%);
        color: #374151;
        font-weight: 600;
        border-bottom: 1px solid #eef0f4;
    }

    :deep(.certificates-table .v-data-table__tr:hover) {
        background: rgba(37, 8, 145, 0.03);
    }

    :deep(.certificates-table .v-data-table__td) {
        border-bottom: 1px solid #f1f3f7;
    }

    .portal-certificates__filters {
        display: grid;
        grid-template-columns: minmax(260px, 1.6fr) minmax(180px, 0.9fr) auto;
        gap: 12px;
        align-items: center;
        border: 1px solid #e8eaf0;
        border-radius: 10px;
        padding: 10px;
        background: #fafbfc;
    }

    .portal-certificates__filters-summary {
        font-size: 0.9rem;
        color: var(--dash-text-muted);
    }

    @media (max-width: 960px) {
        .student-portal-certificates__header {
            margin-bottom: 20px;
            flex-direction: column;
            align-items: stretch;
        }

        .student-portal-certificates__refresh-btn {
            align-self: flex-start;
            margin-top: 0;
        }

        .portal-certificates__filters {
            grid-template-columns: 1fr;
        }
    }

    @media (max-width: 959px) {
        :deep(.certificates-table th:nth-child(2)),
        :deep(.certificates-table td:nth-child(2)) {
            display: none !important;
        }
    }

    .dark.student-portal-certificates :deep(.certificates-table) {
        background-color: var(--dash-bg-elevated);
        color: var(--dash-text);
        border-color: #3d414d;
    }

    .dark.student-portal-certificates :deep(.certificates-table .v-data-table__th) {
        background-color: #252730;
        color: var(--dash-text-muted);
        border-bottom-color: #3d414d;
    }

    .dark.student-portal-certificates :deep(.certificates-table .v-data-table__td) {
        color: var(--dash-text);
        border-bottom-color: #2d313c;
    }

    .dark.student-portal-certificates :deep(.certificates-table .v-data-table__tr:hover) {
        background: rgba(109, 90, 205, 0.12);
    }

    .dark.student-portal-certificates :deep(.v-data-table-footer) {
        background-color: var(--dash-bg-elevated);
        color: var(--dash-text-muted);
        border-color: var(--dash-border);
    }

    .dark.student-portal-certificates .portal-certificates__card--surface {
        border-color: #353945;
        box-shadow:
            0 1px 4px rgba(0, 0, 0, 0.35),
            0 8px 22px rgba(0, 0, 0, 0.24);
    }

    .dark.student-portal-certificates .portal-certificates__filters {
        background: #1e2028;
        border-color: #3d414d;
    }

    .card-title {
        font-size: 28px;
        font-weight: 700;
        color: var(--dash-text);
        margin-bottom: 8px;
    }

    .card-subtitle {
        font-size: 16px;
        color: var(--dash-text-muted);
        margin-bottom: 0;
    }
</style>
